import { of } from 'rxjs';
import { inject } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { CanActivateFn, Router } from '@angular/router';

import { AuthService } from '../services/auth.service';
import { sanitizeReturnUrl } from '../../shared/utils/url.util';

/**
 * Route guard that checks user authentication state within the kiosk namespace.
 * Redirects unauthenticated users to `/kiosk/auth/sign-in`.
 */
export const kioskAuthGuard: CanActivateFn = (_route, state) => {
	const router = inject(Router);
	const authService = inject(AuthService);

	const returnUrl = sanitizeReturnUrl(state.url, '/kiosk/auth/sign-in');

	if (!authService.getAccessToken()) {
		redirectToKioskSignIn(router, returnUrl);
		return of(false);
	}

	if (!authService.isTokenExpired()) {
		return of(true);
	}

	return authService.refreshToken().pipe(
		map(() => true),
		catchError(() => {
			authService.signOut();
			redirectToKioskSignIn(router, returnUrl);
			return of(false);
		})
	);
};

/**
 * Redirects to the kiosk sign-in page with the current URL as returnUrl.
 *
 * @param {Router} router - Angular router instance.
 * @param {string} returnUrl - URL to return to after authentication.
 * @return {void}
 */
function redirectToKioskSignIn(router: Router, returnUrl: string): void {
	void router.navigate(['/kiosk/auth/sign-in'], { queryParams: { returnUrl } });
}
